import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useState } from "react";
import * as XLSX from "xlsx";

import { Button } from "@gately/ui/button";
import { toast } from "@gately/ui/toast";
import { monthRange, periodKind, periodLabel, quarterRange, shiftPeriod } from "@gately/validators";

import { BookingsSection } from "~/components/rapports/bookings-section";
import { ByUnitSection } from "~/components/rapports/by-unit-section";
import { EntryMethodsSection } from "~/components/rapports/entry-methods-section";
import { SecuritySection } from "~/components/rapports/security-section";
import { SectionShell } from "~/components/rapports/section-shell";
import { SummarySection } from "~/components/rapports/summary-section";
import { TrafficSection } from "~/components/rapports/traffic-section";
import { todayKey } from "~/lib/booking-format";
import { orpc, orpcClient } from "~/lib/orpc";
import { reportSheets, scopeLabelFor, scopeSearchFor } from "~/lib/report-format";
import { parseReportSearch, rangeIssue, resolveReportSearch } from "~/lib/report-search";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/rapports")({
  validateSearch: (s: Record<string, unknown>) => parseReportSearch(s),
  // Same gate as /activite; the server enforces it too (assertCanView).
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(orpc.me.get.queryOptions());
    if (!resolvePermissions(me).canViewActivity) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: RapportsPage,
});

const SECTIONS = [
  { id: "synthese", title: "Synthèse" },
  { id: "frequentation", title: "Fréquentation" },
  { id: "par-unite", title: "Par entreprise" }, // title swapped by Terms in the page
  { id: "mode-entree", title: "Mode d'entrée" },
  { id: "securite", title: "Sécurité" },
  { id: "reservations", title: "Réservations" }, // only with the amenities module
] as const;

function RapportsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const timeZone = useOrgTimeZone();
  const terms = useTerms();
  const input = resolveReportSearch(search, todayKey(timeZone));
  const kind = periodKind(input.from, input.to);
  const scopes = useQuery(orpc.report.scopeOptions.queryOptions());
  const summary = useQuery(orpc.report.summary.queryOptions({ input }));
  const traffic = useQuery(orpc.report.traffic.queryOptions({ input }));
  const byUnit = useQuery(orpc.report.byUnit.queryOptions({ input }));
  const entryMethods = useQuery(orpc.report.entryMethods.queryOptions({ input }));
  const security = useQuery(orpc.report.security.queryOptions({ input }));
  const bookings = useQuery(orpc.report.bookings.queryOptions({ input }));

  const [rangeError, setRangeError] = useState<string | null>(null);
  // An invalid range would be dropped by parseReportSearch and snap the page
  // back to last month, so refuse it here and say why.
  const setRange = (range: { from: string; to: string }) => {
    const issue = rangeIssue(range.from, range.to);
    setRangeError(issue);
    if (issue) return;
    void navigate({ search: (s) => ({ ...s, ...range }) });
  };

  const [exporting, setExporting] = useState(false);
  const sectionsSettled = [summary, traffic, byUnit, entryMethods, security, bookings].every((q) => !q.isPending);
  const scopeLabel = scopeLabelFor(search, scopes.data, terms);
  // The unit label / gate name that drill-down links carry; null until resolved.
  const scopeSearch = scopeSearchFor(search, scopes.data);
  // A query that errored with nothing to show gets a « Section indisponible » sheet.
  const failedSection = (q: { isError: boolean; data?: unknown }) => q.isError && !q.data;

  // Audit first: a failed log means no file, so every download leaves a trace.
  async function exportXlsx() {
    setExporting(true);
    try {
      await orpcClient.report.logExport({ ...input, format: "xlsx" });
      const sheets = reportSheets(
        { summary: summary.data, traffic: traffic.data, byUnit: byUnit.data, entryMethods: entryMethods.data, security: security.data, bookings: bookings.data,
          failed: {
            ...(failedSection(summary) && { summary: true as const }),
            ...(failedSection(traffic) && { traffic: true as const }),
            ...(failedSection(byUnit) && { byUnit: true as const }),
            ...(failedSection(entryMethods) && { entryMethods: true as const }),
            ...(failedSection(security) && { security: true as const }),
            ...(failedSection(bookings) && { bookings: true as const }),
          },
        },
        {
          periodLabel: periodLabel(input.from, input.to),
          comparedLabel: summary.data
            ? periodLabel(summary.data.period.previousFrom, summary.data.period.previousTo)
            : "—",
          scopeLabel,
          generatedAt: new Date().toLocaleString("fr-FR", { timeZone }),
        },
        terms,
      );
      const wb = XLSX.utils.book_new();
      for (const sheet of sheets) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet.rows), sheet.name);
      XLSX.writeFile(wb, `rapport-${input.from}_${input.to}.xlsx`);
      toast.success("Export téléchargé.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de l'export.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">Rapports</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Type de période"
            value={kind}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "month") setRange(monthRange(input.from));
              else if (v === "quarter") setRange(quarterRange(input.from));
            }}
            className="h-8 rounded-lg border border-neutral-200 bg-white px-2 text-[13px]"
          >
            <option value="month">Mois</option>
            <option value="quarter">Trimestre</option>
            <option value="custom" disabled={kind !== "custom"}>Personnalisé</option>
          </select>
          <button type="button" aria-label="Période précédente" onClick={() => setRange(shiftPeriod(input.from, input.to, -1))} className="flex size-8 items-center justify-center rounded-lg hover:bg-neutral-100">
            <ChevronLeft size={16} strokeWidth={1.85} />
          </button>
          <span className="min-w-36 text-center text-[13px] font-semibold text-neutral-800">{periodLabel(input.from, input.to)}</span>
          <button type="button" aria-label="Période suivante" onClick={() => setRange(shiftPeriod(input.from, input.to, 1))} className="flex size-8 items-center justify-center rounded-lg hover:bg-neutral-100">
            <ChevronRight size={16} strokeWidth={1.85} />
          </button>
          <input type="date" aria-label="Du" value={input.from} max={input.to} onChange={(e) => e.target.value && setRange({ from: e.target.value, to: input.to })} className="h-8 rounded-lg border border-neutral-200 px-2 text-[13px]" />
          <input type="date" aria-label="Au" value={input.to} min={input.from} onChange={(e) => e.target.value && setRange({ from: input.from, to: e.target.value })} className="h-8 rounded-lg border border-neutral-200 px-2 text-[13px]" />
          {rangeError ? <span role="alert" className="text-[13px] text-red-600">{rangeError}</span> : null}
          <select
            aria-label="Périmètre"
            value={search.unit ? `unit:${search.unit}` : search.gate ? `gate:${search.gate}` : ""}
            onChange={(e) => {
              const [k, id] = e.target.value.split(":");
              void navigate({ search: (s) => ({ from: s.from, to: s.to, ...(k === "unit" ? { unit: id } : k === "gate" ? { gate: id } : {}) }) });
            }}
            className="h-8 max-w-56 rounded-lg border border-neutral-200 bg-white px-2 text-[13px]"
          >
            <option value="">{`Ensemble ${terms.ofTheOrg}`}</option>
            {scopes.data?.units.length ? (
              <optgroup label={terms.unitPlural}>
                {scopes.data.units.map((u) => <option key={u.id} value={`unit:${u.id}`}>{u.label}</option>)}
              </optgroup>
            ) : null}
            {scopes.data?.gates.length ? (
              <optgroup label="Portails">
                {scopes.data.gates.map((g) => <option key={g.id} value={`gate:${g.id}`}>{g.name}</option>)}
              </optgroup>
            ) : null}
          </select>
          <Button variant="outline" size="sm" disabled={exporting || !sectionsSettled || (Boolean(search.unit ?? search.gate) && scopes.isPending)} onClick={() => void exportXlsx()}>
            <Download size={14} strokeWidth={1.85} />
            Exporter XLSX
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        <nav aria-label="Sections" className="sticky top-6 hidden h-fit w-40 shrink-0 flex-col gap-1 lg:flex">
          {SECTIONS.filter((s) => s.id !== "reservations" || bookings.data?.available === true).map((s) => (
            <a key={s.id} href={`#${s.id}`} className="rounded-lg px-2 py-1.5 text-[13px] text-neutral-600 hover:bg-neutral-50">
              {s.id === "par-unite" ? `Par ${terms.unitSingular}` : s.title}
            </a>
          ))}
        </nav>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <SectionShell id="synthese" title="Synthèse" query={summary}>
            {(data) => <SummarySection data={data} scopeSearch={scopeSearch} />}
          </SectionShell>
          <SectionShell id="frequentation" title="Fréquentation" query={traffic}>
            {(data) => <TrafficSection data={data} />}
          </SectionShell>
          <SectionShell id="par-unite" title={`Par ${terms.unitSingular}`} query={byUnit}>
            {(data) => <ByUnitSection data={data} scopeSearch={scopeSearch} />}
          </SectionShell>
          <SectionShell id="mode-entree" title="Mode d'entrée" query={entryMethods}>
            {(data) => <EntryMethodsSection data={data} scopeSearch={scopeSearch} />}
          </SectionShell>
          <SectionShell id="securite" title="Sécurité" query={security}>
            {(data) => <SecuritySection data={data} scopeSearch={scopeSearch} />}
          </SectionShell>
          <SectionShell id="reservations" title="Réservations" query={bookings}>
            {(data) => <BookingsSection data={data} />}
          </SectionShell>
        </div>
      </div>
    </div>
  );
}
